/**
 * Compact timestamp formatter used by every admin table per the
 * `feedback-admin-table-baseline` convention:
 *
 *   - same year → `DD.MM HH:MM`        (e.g. `27.05 14:32`)
 *   - cross-year → `DD.MM.YY HH:MM`    (e.g. `04.11.25 09:05`)
 *   - null/undefined input → `—`       (em dash)
 *
 * The full ISO timestamp belongs on the tooltip; callers render it
 * themselves via `formatFullIso`.
 */

function pad2(n: number): string {
  return n < 10 ? `0${n}` : String(n);
}

export function formatCompactDateTime(value: string | null | undefined): string {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "—";
  const day = pad2(d.getDate());
  const month = pad2(d.getMonth() + 1);
  const hour = pad2(d.getHours());
  const min = pad2(d.getMinutes());
  const currentYear = new Date().getFullYear();
  if (d.getFullYear() === currentYear) {
    return `${day}.${month} ${hour}:${min}`;
  }
  const yy = String(d.getFullYear() % 100).padStart(2, "0");
  return `${day}.${month}.${yy} ${hour}:${min}`;
}

export function formatFullIso(value: string | null | undefined): string {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "—";
  // Local-timezone full timestamp; the operator usually wants their own
  // wall-clock when hovering, not UTC. Name retained for backward
  // compatibility but the output is German DD.MM.YYYY HH:MM:SS — the
  // admin frontend is unambiguously German per project policy.
  return `${pad2(d.getDate())}.${pad2(d.getMonth() + 1)}.${d.getFullYear()} ${pad2(d.getHours())}:${pad2(d.getMinutes())}:${pad2(d.getSeconds())}`;
}

export function formatCents(c: number | null | undefined): string {
  if (c === null || c === undefined) return "—";
  const euros = c / 100;
  return new Intl.NumberFormat("de-DE", {
    style: "currency",
    currency: "EUR",
  }).format(euros);
}
